/**
 * Pipeline node execution tracking — read-only access to per-node execution
 * status, timing, and output data within pipeline executions.
 */

// Generated functions — verify names against sdk.gen.ts after mix update.sdks
import {
  getAdminPipelineNodeExecutionsById,
  getAdminPipelineNodeExecutionsByExecutionByPipelineExecutionId,
} from "../_internal/sdk.gen";
import type { RequestOptions } from "../base-client";
import type { RequestBuilder } from "../request-builder";

/** Node execution status */
export type PipelineNodeExecutionStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "skipped";

/** Pipeline node execution response type */
export interface PipelineNodeExecution {
  id: string;
  pipeline_execution_id: string;
  pipeline_node_id: string;
  node_key: string;
  node_type: "prompt" | "command" | "connector_exec" | "checkpoint";
  phase?: "define" | "plan" | "build" | "verify" | "review" | "ship" | null;
  status: PipelineNodeExecutionStatus;
  agent_execution_id?: string | null;
  input?: Record<string, unknown> | null;
  output?: Record<string, unknown> | null;
  error?: Record<string, unknown> | null;
  tokens_used?: number | null;
  credits_charged?: string | null;
  duration_ms?: number | null;
  started_at?: string | null;
  completed_at?: string | null;
  quality_scores?: Record<string, unknown> | null;
  criteria?: Array<Record<string, unknown>> | null;
  inserted_at: string;
  updated_at: string;
}

/**
 * Creates the pipeline node executions namespace with read-only operations.
 *
 * @example
 * ```typescript
 * const nodeExecs = await admin.pipelineNodeExecutions.listByExecution(executionId);
 * const nodeExec = await admin.pipelineNodeExecutions.get(nodeExecId);
 * ```
 */
export function createPipelineNodeExecutionsNamespace(rb: RequestBuilder) {
  return {
    /**
     * Get a pipeline node execution by ID.
     *
     * @param id - Pipeline node execution UUID
     * @param options - Request options
     * @returns The pipeline node execution
     */
    async get(
      id: string,
      options?: RequestOptions,
    ): Promise<PipelineNodeExecution> {
      const response = await rb.execute<PipelineNodeExecution>(
        getAdminPipelineNodeExecutionsById,
        {
          params: { path: { id } },
          ...options,
        },
      );
      return response;
    },

    /**
     * List node executions for a pipeline execution.
     *
     * @param pipelineExecutionId - Pipeline execution UUID
     * @param options - Request options
     * @returns Array of node executions for the pipeline execution
     */
    async listByExecution(
      pipelineExecutionId: string,
      options?: RequestOptions,
    ): Promise<PipelineNodeExecution[]> {
      const response = await rb.execute<PipelineNodeExecution[]>(
        getAdminPipelineNodeExecutionsByExecutionByPipelineExecutionId,
        {
          params: { path: { pipeline_execution_id: pipelineExecutionId } },
          ...options,
        },
      );
      return response;
    },
  };
}
